<template>
  <div v-if="banners.length > 0" class="banners">

    <!-- CAROUSEL PRINCIPAL (HERO) -->
    <div id="carouselExampleIndicators" ref="carouselEl" class="carousel slide custom-carousel carousel-fade">
      <!-- Slides -->
      <div class="carousel-inner">
        <div v-for="(banner, index) in banners" :key="'slide-' + index" class="carousel-item"
          :class="{ active: index === 0 }" :data-bs-interval="banner._interval">

          <!-- Imagen -->
          <picture>
            <!-- solo si existe mobile -->
            <source v-if="banner.cachedImageMobile" media="(max-width: 767px)" :srcset="banner.cachedImageMobile" />
            <!-- fallback siempre -->
            <img :src="banner.cachedImage" class="d-block w-100 imgbanner" :alt="banner.description" loading="lazy" />
          </picture>

          <!-- Contenido -->
          <div class="carousel-caption hero-caption">

            <!-- TÍTULO -->
            <div class="hero-row hero-title">
              <h5>{{ banner.description }}</h5>
            </div>

            <!-- TIMER -->
            <div class="hero-row hero-timer" v-if="banner.timer_activo">
              <div class="countdown-timer">
                <span v-if="banner.countdown !== 'Expirado'">
                  ⏳ Oferta termina en: <strong>{{ banner.countdown }}</strong>
                </span>
                <span v-else class="text-danger fw-bold">
                  🔥 Oferta expirada
                </span>
              </div>
            </div>

            <!-- CTA -->
            <div class="hero-row hero-cta">
              <template v-if="banner.tipo === 'NORMAL' && banner.id_linea">
                <router-link :to="{ name: 'ListadoProductos', query: { id: banner.id_linea, linea: banner.linea } }"
                  class="cta-button">
                  Explorar línea
                </router-link>
              </template>

              <template v-if="banner.tipo === 'MEDIA' && banner.url">
                <a :href="banner.url" class="cta-button" target="_blank" rel="noopener">
                  Ver contenido
                </a>
              </template>
            </div>

            <!-- INDICADORES (SIEMPRE ABAJO) -->
            <div class="hero-row hero-progress">
              <div class="progress-indicator">
                <div class="pause-icon" @click="togglePause">
                  {{ isPaused ? '▶' : '||' }}
                </div>

                <div class="progress-list">
                  <div v-for="(b, i) in banners" :key="'progress-' + i" class="progress-bar"
                    @click="selectBannerFromProgress(i)">
                    <div class="progress-fill" :style="{
                      width:
                        i < selectedBanner
                          ? '100%'
                          : i === selectedBanner
                            ? bannerProgress + '%'
                            : '0%'
                    }"></div>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- BARRA STICKY (PROMO PERSISTENTE AL HACER SCROLL) -->
    <transition name="fade-slide">
      <div v-if="showStickyPromo && activePromo" class="sticky-promo-bar shadow"
        :style="{ top: stickyTopOffset + 'px' }">
        <div class="container d-flex align-items-center justify-content-between py-2">

          <div class="d-flex align-items-center gap-2">
            <span class="badge bg-danger pulse-animation">PROMO</span>
            <span class="text-white fw-bold d-none d-md-inline">{{ activePromo.description }}</span>
          </div>

          <div class="countdown-mini text-white">
            <span class="d-none d-sm-inline">Termina en:</span>
            <strong class="text-warning ml-1">{{ activePromo.countdown }}</strong>
          </div>

          <router-link :to="{ name: 'ListadoProductos', query: { id: activePromo.id_linea, linea: activePromo.linea } }"
            class="btn btn-sm btn-light fw-bold rounded-pill px-3">
            Ver ahora
          </router-link>

        </div>
      </div>
    </transition>

  </div>
</template>

<script>
import Carousel from 'bootstrap/js/dist/carousel';
import { cacheImage, getCachedImage } from '../../../../../utils/imageCache.js';

export default {
  data() {
    return {
      selectedBanner: 0,
      banners: [],
      carouselInstance: null,
      timerInterval: null,
      progressInterval: null,
      bannerProgress: 0,
      isPaused: false,
      showStickyPromo: false,
      stickyTopOffset: 0,
      navbarResizeObserver: null,
    };
  },

  computed: {
    activePromo() {
      if (this.banners.length === 0) return null;
      const current = this.banners[this.selectedBanner];
      if (current && current.timer_activo) return current;
      return this.banners.find(b => b.timer_activo);
    }
  },

  methods: {
    handleScroll() {
      const scrollY = window.scrollY;
      const heroHeight = this.$refs.carouselEl?.offsetHeight || 500;
      const triggerPoint = heroHeight - this.stickyTopOffset;

      if (scrollY > triggerPoint) {
        if (!this.showStickyPromo) {
          const currentBanner = this.banners[this.selectedBanner];
          const isCurrentValid = currentBanner?.timer_activo && currentBanner?.countdown !== 'Expirado';

          if (!isCurrentValid) {
            const promoIndex = this.banners.findIndex(b => b.timer_activo && b.countdown !== 'Expirado');

            if (promoIndex !== -1) {
              this.carouselInstance.to(promoIndex);
              this.showStickyPromo = true;
            } else {
              this.showStickyPromo = false;
            }
          } else {
            this.showStickyPromo = true;
          }

          if (this.showStickyPromo && !this.isPaused) this.togglePause();
        }
      } else {
        if (this.showStickyPromo) {
          this.showStickyPromo = false;
          if (this.isPaused) this.togglePause();
        }
      }
    },

    observarNavbar() {
      const navbar = document.getElementById('main-navbar');
      if (!navbar) return;

      this.navbarResizeObserver = new ResizeObserver(() => {
        const rect = navbar.getBoundingClientRect();
        this.stickyTopOffset = rect.height;
      });

      this.navbarResizeObserver.observe(navbar);
    },

    getInterval(banner) {
      if (banner?.duration_seg && !isNaN(Number(banner.duration_seg))) {
        return Number(banner.duration_seg) * 1000;
      }
      if (!banner?.image) return 5000;
      const ext = banner.image.toLowerCase();
      if (ext.endsWith('.gif')) return 8000;
      return 5000;
    },

    formatCountdown(targetDate) {
      if (!targetDate) return null;
      const now = new Date();
      const diff = new Date(targetDate) - now;
      if (diff <= 0) return "Expirado";

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
      const minutes = Math.floor((diff / (1000 * 60)) % 60);
      const seconds = Math.floor((diff / 1000) % 60);

      return `${days}d ${hours}h ${minutes}m ${seconds}s`;
    },

    startCountdowns() {
      if (this.timerInterval) clearInterval(this.timerInterval);
      this.timerInterval = setInterval(() => {
        this.banners = this.banners.map(b => ({
          ...b,
          countdown: b.fecha_vencimiento ? this.formatCountdown(b.fecha_vencimiento) : null,
        }));
      }, 1000);
    },

    startProgress() {
      if (this.progressInterval) clearInterval(this.progressInterval);
      if (this.isPaused) return;

      this.bannerProgress = 0;
      const currentBanner = this.banners[this.selectedBanner];
      const interval = currentBanner?._interval || 5000;
      const stepTime = 50;
      const increment = (stepTime / interval) * 100;

      this.progressInterval = setInterval(() => {
        if (!this.isPaused) {
          this.bannerProgress += increment;
          if (this.bannerProgress >= 100) {
            clearInterval(this.progressInterval);
            if (this.carouselInstance) this.carouselInstance.next();
          }
        }
      }, stepTime);
    },

    togglePause() {
      this.isPaused = !this.isPaused;
      if (this.isPaused) {
        if (this.progressInterval) clearInterval(this.progressInterval);
        if (this.carouselInstance) this.carouselInstance.pause();
      } else {
        this.startProgress();
        if (this.carouselInstance) this.carouselInstance.cycle();
      }
    },

    async obtenerBanners() {
      try {
        const response = await axios.get('/api/banners');
        const rawBanners = response.data || [];

        const processed = await Promise.all(
          rawBanners.map(async (b) => {
            const desktopUrl = `img/banners/${b.image}`;
            const mobileUrl = b.imagen_mobile ? `img/banners/${b.imagen_mobile}` : null;

            const resolveCache = async (url) => {
              if (!url) return null;
              const cached = await getCachedImage(url);
              if (cached) return cached;
              await cacheImage(url);
              return url;
            };

            return {
              ...b,
              _interval: this.getInterval(b),
              countdown: b.fecha_vencimiento ? this.formatCountdown(b.fecha_vencimiento) : null,
              cachedImage: await resolveCache(desktopUrl),
              cachedImageMobile: await resolveCache(mobileUrl)
            }
          })
        );

        this.banners = processed;

        if (this.carouselInstance) {
          this.carouselInstance.dispose();
          this.carouselInstance = null;
          this.$refs.carouselEl?.removeEventListener('slid.bs.carousel', this.handleSlide);
        }

        await this.$nextTick();

        const el = this.$refs.carouselEl;
        if (!el) return;

        this.carouselInstance = new Carousel(el, {
          interval: false, ride: false, pause: false, wrap: true, touch: true,
        });

        el.addEventListener('slid.bs.carousel', this.handleSlide);
        this.selectedBanner = 0;
        this.bannerProgress = 0;

        setTimeout(() => { this.startProgress(); }, 50);

        if (this.banners.some(b => b.fecha_vencimiento)) {
          this.startCountdowns();
        }

      } catch (error) {
        console.error('Error fetching banners:', error);
      }
    },

    handleSlide() {
      const el = this.$refs.carouselEl;
      if (!el) return;
      const items = Array.from(el.querySelectorAll('.carousel-item'));
      const idx = items.findIndex(i => i.classList.contains('active'));

      if (idx === this.selectedBanner) return;

      this.selectedBanner = idx;
      this.bannerProgress = 0;
      this.startProgress();
    },

    selectBannerFromProgress(index) {
      if (this.carouselInstance) this.carouselInstance.to(index);
      this.bannerProgress = 0;
      this.isPaused = false;
      this.startProgress();
    }
  },

  mounted() {
    this.obtenerBanners();
    this.observarNavbar();
    window.addEventListener('scroll', this.handleScroll);
  },

  unmounted() {
    const el = this.$refs.carouselEl;
    if (el) el.removeEventListener('slid.bs.carousel', this.handleSlide);

    if (this.carouselInstance) {
      try { this.carouselInstance.dispose(); } catch (e) { }
      this.carouselInstance = null;
    }

    if (this.timerInterval) clearInterval(this.timerInterval);
    if (this.progressInterval) clearInterval(this.progressInterval);

    // Limpieza de eventos nuevos
    window.removeEventListener('scroll', this.handleScroll);
    if (this.navbarResizeObserver) this.navbarResizeObserver.disconnect();
  },
};
</script>

<style scoped>
/* --- ESTILOS ORIGINALES DEL HERO --- */
.custom-carousel {
  overflow: hidden;
  max-height: 80vh;
}

.imgbanner {
  height: 600px;
  width: 100%;
}

.carousel-caption {
  bottom: 20%;
  text-shadow: 1px 1px 6px rgba(0, 0, 0, 0.6);
}

.cta-button {
  position: relative;
  z-index: 20;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.6rem 1.5rem;
  font-size: 16px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  border-radius: 50px;
  background: linear-gradient(135deg, #06283c, #0a4b6f);
  color: #fff;
  text-decoration: none;
  margin: 12px 0;
  transition: transform 0.25s ease, box-shadow 0.25s ease, background 0.3s ease;
}

.cta-button:hover {
  transform: translateY(-2px) scale(1.04);
  background: linear-gradient(135deg, #0a4b6f, #06283c);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.28);
}

.cta-button:active {
  transform: translateY(0) scale(0.98);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.22);
}

.cta-button::after {
  content: "";
  position: absolute;
  inset: -15%;
  background: rgba(255, 255, 255, 0.18);
  border-radius: inherit;
  transform: scale(0);
  transition: transform 0.3s ease;
  pointer-events: none;
}

.cta-button:hover::after {
  transform: scale(1);
}

/* --- INDICADOR DE PROGRESO --- */
.progress-indicator {
  position: absolute;
  bottom: 15px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 10px;
  width: 60%;
}

.pause-icon {
  cursor: pointer;
  font-weight: bold;
  color: #fff;
}

.progress-list {
  display: flex;
  gap: 8px;
  flex: 1;
}

.progress-bar {
  flex: 1;
  height: 4px;
  background-color: rgba(255, 255, 255, 0.25);
  border-radius: 2px;
  overflow: hidden;
  cursor: pointer;
}

.progress-bar:hover {
  background-color: rgba(255, 255, 255, 0.4);
}

.progress-fill {
  height: 100%;
  background-color: #1ca3df;
  /* Cambiado a un tono Cyan para resaltar sobre el oscuro */
  width: 0%;
  transition: width 0.05s linear;
}

.countdown-timer {
  background: rgba(0, 0, 0, 0.6);
  display: inline-block;
  padding: 6px 12px;
  border-radius: 8px;
  color: #fff;
  font-weight: 500;
}

.countdown-timer strong {
  color: #ffcc00;
}

.hero-caption {
  position: absolute;
  left: 5%;
  right: 5%;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  pointer-events: auto;
}

.hero-row {
  width: 100%;
  display: flex;
  justify-content: center;
}

.hero-title {
  margin-bottom: 12px;
}

.hero-timer {
  margin-bottom: 16px;
}

.hero-title h5 {
  max-width: 80%;
  margin: 0 auto;
  font-size: clamp(1.1rem, 4vw, 2rem);
}

.hero-cta {
  margin-bottom: 22px;
}

.hero-progress {
  position: absolute;
  bottom: 12px;
  left: 0;
  right: 0;
}

/* --- NUEVOS ESTILOS: SMART STICKY BAR --- */
.sticky-promo-bar {
  position: fixed;
  left: 0;
  right: 0;
  /* El top se asigna por JS dinámicamente con :style */
  z-index: 990;
  /* Tu navbar debería tener 1000 o más */
  height: auto;
  /* Que el contenido defina el alto o usa uno fijo como 50px */
  min-height: 50px;
  display: flex;
  align-items: center;
  background: rgba(0, 0, 0, 0.6);
  border-bottom: 2px solid #1ca3df;
  /* Esto evita que la barra "tiemble" al aparecer */
  will-change: transform, top;
}

.countdown-mini strong {
  color: #ffcc00;
  font-family: 'Courier New', Courier, monospace;
}

/* Efecto Pulse para el badge de PROMO */
.pulse-animation {
  animation: pulse-red 2s infinite;
  box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.7);
}

@keyframes pulse-red {
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.7);
  }

  70% {
    transform: scale(1);
    box-shadow: 0 0 0 10px rgba(220, 53, 69, 0);
  }

  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(220, 53, 69, 0);
  }
}

/* Transiciones de la barra */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
}

.fade-slide-enter-from,
.fade-slide-leave-to {
  transform: translateY(-100%);
  opacity: 0;
}

/* --- MÓVILES --- */
@media (max-width: 768px) {
  .imgbanner {
    height: 280px;
    object-fit: cover;
    /* Cambiado a cover para no deformar piezas automotrices */
  }

  .carousel-caption {
    bottom: 5%;
  }

  .cta-button {
    font-size: 14px;
    padding: 0.5rem 1rem;
  }

  .progress-indicator {
    width: 80%;
    bottom: 10px;
  }

  .sticky-promo-bar .btn {
    padding: 0.25rem 0.6rem;
    font-size: 12px;
  }
}
</style>